import { Icon, Label, VideoModal } from '@aibees/shared/components';
import { LOOM_CREDIT_USAGE_GUIDE_EMBED } from '@aibees/shared/constants';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Box, Button, Typography } from '@mui/material';
import { memo, useCallback, useState } from 'react';
import { PlanDetailProvider } from '../plan-detail/context';
import { CreditCalculator } from '../plan-detail/components/how-credits-work/credit-calculator';
import { CreditUsageBgVideo } from './components/credit-usage-bg-video';
import { useCreditRateCategories } from './hooks/use-credit-rate-categories';
import * as SX from './sx';
import type { CreditUsageOverviewProps as Props } from './types';

export const CreditUsageOverview = memo(({ className }: Props) => {
  const { categories } = useCreditRateCategories();
  const videoState = useBoolean(false);
  const [calculatorOpen, setCalculatorOpen] = useState(false);

  const handleOpenCalculator = useCallback(() => setCalculatorOpen(true), []);
  const handleCloseCalculator = useCallback(() => setCalculatorOpen(false), []);

  return (
    <PlanDetailProvider>
      <Box
        className={classnames('CreditUsageOverview', className)}
        sx={SX.root}
      >
        <Box sx={SX.leftColumn}>
          <Box sx={SX.headSection}>
            <Label color="primary" variant="soft" size="small">
              Credits
            </Label>
            <Typography variant="h5" color="grey.900">
              How Credits Work?
            </Typography>
            <Typography variant="body4" color="grey.500">
              Each action costs credits based on its complexity.
            </Typography>
          </Box>

          <Box sx={SX.videoWrapper}>
            <CreditUsageBgVideo />
            <Box sx={SX.videoContent}>
              <Box sx={SX.videoTextWrapper}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="common.white"
                >
                  Understanding Credit Usage
                </Typography>
                <Typography variant="body5" color="grey.400">
                  Learn how usage is calculated in just 60 seconds
                </Typography>
              </Box>
              <Button
                size="small"
                color="primary"
                variant="contained"
                startIcon={<Icon name="play-video" size={16} />}
                onClick={videoState.onTrue}
                disableElevation
              >
                Watch the Video
              </Button>
            </Box>
          </Box>

          <VideoModal
            state={videoState}
            videoUrl={LOOM_CREDIT_USAGE_GUIDE_EMBED}
            title="Understanding Credit Usage"
            titleIcon="play-video"
          />
        </Box>

        <Box sx={SX.rightColumn}>
          <Box sx={SX.headSection}>
            <Label color="primary" variant="soft" size="small">
              Estimate
            </Label>
            <Typography variant="h5" color="grey.900">
              Estimate Your Credit Usage
            </Typography>
            <Typography variant="body4" color="grey.500">
              Get a clear estimate of the credits you&apos;ll need each month
              based on your usage.
            </Typography>
          </Box>

          <Box sx={SX.creditsContent}>
            <Box sx={SX.estimateHeader}>
              <Typography variant="body2" fontWeight={600} color="grey.900">
                Estimate Monthly Credits
              </Typography>
              <Button
                color="primary"
                size="small"
                variant="contained"
                startIcon={<Icon name="calculator" size={16} />}
                onClick={handleOpenCalculator}
                disableElevation
              >
                Open Credit Calculator
              </Button>
            </Box>

            <Box sx={SX.categoriesGrid}>
              {categories.map((category) => (
                <Box key={category.title} sx={SX.categoryCard}>
                  <Box sx={SX.categoryHeader}>
                    <Box sx={SX.categoryIconBox}>
                      <Icon
                        name={category.icon}
                        size={22}
                        color="primary.main"
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="grey.700"
                      noWrap
                    >
                      {category.title}
                    </Typography>
                  </Box>
                  <Box sx={SX.categoryDivider} />
                  <Box sx={SX.rowsWrap}>
                    {category.rows.map((row) => (
                      <Box key={row.type} sx={SX.creditRow}>
                        <Box sx={SX.creditRowLeft}>
                          <Icon
                            name="info-circle-outline"
                            size={18}
                            color="grey.500"
                          />
                          <Typography
                            variant="body4"
                            color="common.black"
                            noWrap
                          >
                            {row.title}
                          </Typography>
                        </Box>
                        <Box sx={SX.creditPill}>
                          <Icon name="coin" size={16} color="primary.main" />
                          <Typography variant="body4" color="primary.600">
                            x {row.credit}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
      <CreditCalculator open={calculatorOpen} onClose={handleCloseCalculator} />
    </PlanDetailProvider>
  );
});

CreditUsageOverview.displayName = 'CreditUsageOverview';
